import { showSpace, hide } from "@intercom/messenger-js-sdk";
import { DOC_URLS, HIGHARC_DOCS_NAME, LOCAL_STORAGE_KEYS, WEB_API_BASE_PATH } from "common/constants";
import { TEST_E2E_MODE } from "common/constants/env";
import Flags from "common/constants/flags";
import { INTERCOM_LINK_ID } from "common/constants/marketing";
import type { SearchArticlesResponse } from "intercom-client/api";
import DebugInfoModal from "lib/components/builders/DebugInfoModal";
import { useFetch } from "lib/fetcher";
import { useUserContext } from "lib/state/UserContextProvider";
import { type LiHTMLAttributes, useRef, useState } from "react";
import DropdownMenu from "ui/components/DropdownMenu";
import Link from "ui/components/Link";
import cx from "ui/cx";
import useLocalStorage from "ui/hooks/useLocalStorage";
import IconInfoLine from "ui/icons/IconInfoLine";
import IconNotification from "ui/icons/IconNotification";
import IconQuestionMarkCircle from "ui/icons/IconQuestionMarkCircle";
import styles from "./ActionMenu.module.scss";

interface ActionItemProps extends LiHTMLAttributes<HTMLLIElement> {
  icon: React.ReactNode;
  onClick?: () => any;
  disabled?: boolean;
}

const INTERCOM_DISABLED = Flags.IS_DEV_MODE || TEST_E2E_MODE;

// In preparation for adding App Switcher and Notifications
export default function ActionMenu() {
  const { user } = useUserContext();
  const [intercomOpen, setIntercomOpen] = useState(false);
  const [showDebugInfoModal, setShowDebugInfoModal] = useState(false);
  const [intercomReleaseNotesCount, setIntercomReleaseNotesCount] = useLocalStorage(
    LOCAL_STORAGE_KEYS.RELEASE_NOTES_COUNT,
    0,
  );
  const canViewDebugInfo = user?.adminRole === "SUPER_ADMIN" || user?.adminRole === "SYSTEM_ADMIN";

  // check for unread articles and set the count
  const { data } = useFetch<SearchArticlesResponse>(`${WEB_API_BASE_PATH}/public/release-notes`, [!INTERCOM_DISABLED]);
  const totalCount = data?.total_count || 0;

  return (
    <>
      <ul className={styles.wrapper}>
        <ActionItem
          id={INTERCOM_LINK_ID}
          disabled={INTERCOM_DISABLED}
          title={!INTERCOM_DISABLED ? "Open Release Notes" : "Release Notes are disabled in DEV_MODE"}
          icon={<IconNotification size={16} showBadge={intercomReleaseNotesCount < totalCount} />}
          onClick={() => {
            if (intercomOpen) {
              hide();
              setIntercomOpen(false);
            } else {
              showSpace("help");
              setIntercomOpen(true);
            }

            setIntercomReleaseNotesCount(totalCount);
          }}
        />
        <ActionItem icon={<IconQuestionMarkCircle size={16} />}>
          <Link href={DOC_URLS.HIGHARC_DOCS.BASE} target="_blank">
            {HIGHARC_DOCS_NAME}
          </Link>
        </ActionItem>
        {canViewDebugInfo ? (
          <ActionItem
            title="Debugging Info"
            icon={<IconInfoLine size={16} />}
            onClick={() => setShowDebugInfoModal(true)}
          />
        ) : null}
      </ul>
      {showDebugInfoModal ? (
        <DebugInfoModal open={showDebugInfoModal} onClose={() => setShowDebugInfoModal(false)} />
      ) : null}
    </>
  );
}

function ActionItem({ id, icon, onClick, children, disabled, ...props }: ActionItemProps) {
  const [isOpen, setIsOpen] = useState(false);
  const opener = useRef<HTMLLIElement>(null);

  return (
    <li
      {...props}
      id={id}
      ref={opener}
      onClick={
        !disabled
          ? () => {
              onClick?.();
              children && setIsOpen((prev) => !prev);
            }
          : undefined
      }
      className={cx(disabled && styles.disabled)}
    >
      {icon}
      {isOpen && children && (
        <DropdownMenu opener={opener} onClose={() => setIsOpen(false)}>
          {children}
        </DropdownMenu>
      )}
    </li>
  );
}
